import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import PageHero from "../components/PageHero";
import ProductCard from "../components/ProductCard";
import { getMenu, rupee } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function MenuPage() {
  const [data, setData] = useState({ categories: [], items: [] });
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const { count, total, setOpen } = useCart();

  useEffect(() => {
    getMenu()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  const tabs = ["All", ...data.categories];
  const filtered = useMemo(
    () => (active === "All" ? data.items : data.items.filter((i) => i.category === active)),
    [active, data]
  );

  return (
    <div data-testid="menu-page" className="pb-32">
      <PageHero
        testid="menu-hero"
        eyebrow="16 dishes & counting"
        title="The"
        accent="Menu"
        subtitle="Marinated for 12 hours, fired over coal, plated for the feed. Pick your poison — every price includes our promise of quality."
      />

      {/* Category pills */}
      <div className="sticky top-[74px] z-30 bg-brown/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex gap-2 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button
              key={t}
              data-testid={`menu-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => setActive(t)}
              className={`shrink-0 px-5 py-2 rounded-full font-bold uppercase text-sm transition-colors border ${
                active === t
                  ? "bg-orange text-primary-foreground border-orange"
                  : "bg-transparent text-cream/70 border-border hover:border-orange hover:text-cream"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 rounded-lg bg-card animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="menu-grid">
            {filtered.map((item) => (
              <ProductCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* Sticky bottom cart bar */}
      {count > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-4 inset-x-4 z-40 max-w-md mx-auto"
        >
          <button
            data-testid="menu-view-cart-bar"
            onClick={() => setOpen(true)}
            className="w-full flex items-center justify-between bg-orange text-primary-foreground font-bold uppercase px-5 py-4 rounded-lg shadow-hard-cream hover:scale-[1.02] transition-transform"
          >
            <span className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" /> {count} {count === 1 ? "item" : "items"}
            </span>
            <span>View Cart · {rupee(total)}</span>
          </button>
        </motion.div>
      )}
    </div>
  );
}
